import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

// Lazy load Three.js components
const Globe3D = lazy(() => import('./Globe3D'));
const WorldMap = lazy(() => import('./WorldMap'));

// Lazy load SVG visualization components
const SecurityViz = lazy(() => import('./SecurityViz'));
const OnRampViz = lazy(() => import('./OnRampViz'));
const OffRampViz = lazy(() => import('./OffRampViz'));

// Loading fallback component
const ThreeLoadingFallback = ({ height = '320px' }: { height?: string }) => (
  <div 
    className="relative w-full flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden"
    style={{ height }}
  >
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      <span className="text-xs text-muted-foreground">Loading visualization...</span>
    </div>
  </div>
);

// Hook for viewport-based loading
const useInView = (rootMargin = '100px') => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin]);

  return { ref, isInView };
};

// Lazy Globe3D with viewport-based loading
export const LazyGlobe3D = () => {
  const { ref, isInView } = useInView('200px');
  
  return (
    <div ref={ref} className="w-full h-full min-h-[320px]">
      {isInView ? (
        <Suspense fallback={<ThreeLoadingFallback />}>
          <Globe3D />
        </Suspense>
      ) : (
        <ThreeLoadingFallback />
      )}
    </div>
  );
};

// Lazy WorldMap with viewport-based loading
export const LazyWorldMap = () => {
  const { ref, isInView } = useInView('200px');
  
  return (
    <div ref={ref} className="w-full h-full min-h-[320px]">
      {isInView ? (
        <Suspense fallback={<ThreeLoadingFallback />}>
          <WorldMap />
        </Suspense>
      ) : (
        <ThreeLoadingFallback />
      )}
    </div>
  );
};


// Lazy SecurityViz with viewport-based loading
export const LazySecurityViz = () => {
  const { ref, isInView } = useInView('200px');
  
  return (
    <div ref={ref} className="w-full h-full min-h-[240px]">
      {isInView ? (
        <Suspense fallback={<ThreeLoadingFallback height="240px" />}>
          <SecurityViz />
        </Suspense>
      ) : (
        <ThreeLoadingFallback height="240px" />
      )}
    </div>
  );
};

// Lazy OnRampViz with viewport-based loading
export const LazyOnRampViz = () => {
  const { ref, isInView } = useInView('200px');
  
  return (
    <div ref={ref} className="w-full h-full min-h-[200px]">
      {isInView ? (
        <Suspense fallback={<ThreeLoadingFallback height="200px" />}>
          <OnRampViz />
        </Suspense>
      ) : (
        <ThreeLoadingFallback height="200px" />
      )}
    </div>
  );
};

// Lazy OffRampViz with viewport-based loading
export const LazyOffRampViz = () => {
  const { ref, isInView } = useInView('200px');
  
  return (
    <div ref={ref} className="w-full h-full min-h-[200px]">
      {isInView ? (
        <Suspense fallback={<ThreeLoadingFallback height="200px" />}>
          <OffRampViz />
        </Suspense>
      ) : (
        <ThreeLoadingFallback height="200px" />
      )}
    </div>
  );
};

export { ThreeLoadingFallback };
